import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Lock, CheckCircle } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const MitarbeiterSetPasswordPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });

  const employeeId = searchParams.get('id');

  useEffect(() => {
    if (!employeeId) {
      toast({
        title: "Fehler",
        description: "Ungültiger Einladungslink",
        variant: "destructive"
      });
      navigate('/mitarbeiter-login');
    }
  }, [employeeId, navigate, toast]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast({
        title: "Fehler",
        description: "Passwörter stimmen nicht überein",
        variant: "destructive"
      });
      return;
    }

    if (formData.password.length < 6) {
      toast({
        title: "Fehler",
        description: "Passwort muss mindestens 6 Zeichen haben",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/mitarbeiter/set-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          employeeId: employeeId,
          password: formData.password
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setSuccess(true);
        toast({
          title: "Erfolg!",
          description: "Ihr Passwort wurde gesetzt"
        });
      } else {
        toast({
          title: "Fehler",
          description: data.detail || "Passwort konnte nicht gesetzt werden",
          variant: "destructive"
        });
      }
    } catch (error) {
      toast({
        title: "Fehler",
        description: "Netzwerkfehler",
        variant: "destructive"
      });
    }
    
    setLoading(false);
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black flex items-center justify-center p-6">
        <Card className="w-full max-w-md bg-gradient-to-br from-gray-900 to-black border-green-500/30">
          <CardContent className="pt-8 text-center">
            <div className="h-16 w-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="h-8 w-8 text-green-500" />
            </div>
            <h2 className="text-xl font-bold text-white mb-2">Passwort erfolgreich gesetzt!</h2>
            <p className="text-gray-400 mb-6">Sie können sich jetzt mit Ihrer E-Mail und Ihrem neuen Passwort anmelden.</p>
            <Button 
              onClick={() => navigate('/mitarbeiter-login')}
              className="bg-yellow-500 text-black hover:bg-yellow-600"
            >
              Zum Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-center gap-2 mb-8 cursor-pointer" onClick={() => navigate('/')}>
          <img src="/assets/smartvolt-logo.png" alt="Smartvolt Academy" className="h-16" />
        </div>

        <Card className="bg-gradient-to-br from-gray-900 to-black border-yellow-600/20">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <div className="h-16 w-16 rounded-full bg-yellow-500/20 flex items-center justify-center">
                <Lock className="h-8 w-8 text-yellow-500" />
              </div>
            </div>
            <CardTitle className="text-2xl text-white">Passwort festlegen</CardTitle>
            <CardDescription className="text-gray-400">Willkommen! Legen Sie Ihr persönliches Passwort fest.</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="password" className="text-gray-300">Neues Passwort</Label>
                <Input
                  id="password"
                  type="password"
                  required
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  className="bg-gray-800 border-yellow-600/20 text-white focus:border-yellow-500"
                  placeholder="Mindestens 6 Zeichen"
                />
              </div>
              <div>
                <Label htmlFor="confirmPassword" className="text-gray-300">Passwort bestätigen</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  required
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
                  className="bg-gray-800 border-yellow-600/20 text-white focus:border-yellow-500"
                  placeholder="Passwort wiederholen"
                />
              </div>
              <Button type="submit" disabled={loading} className="w-full bg-gradient-to-r from-yellow-600 to-yellow-500 text-black font-semibold hover:from-yellow-500 hover:to-yellow-400">
                {loading ? 'Wird gespeichert...' : 'Passwort speichern'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default MitarbeiterSetPasswordPage;
